import { memo, useCallback, useState } from "react";
import { Security } from "../../../../proto/generated/security";
import AnalyticsManager from "../../../../analytics/AnalyticsManager";
import { ExtraSmallButton } from "../../../custom";
import { Box, MenuItem, MenuItemProps, Popover } from "@mui/material";
import { createStringDateFromStamp } from "../../../../util/tradePage";

export type ExpiryCellProps = {
    security: Security,
    disabled?: boolean,
    expiries: {label: string, value: number}[],
    onChange?: (expiry: number) => void
}

const ExpiryCell: React.FC<ExpiryCellProps> = ({security, expiries, disabled, onChange}) => {

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>(null)

    const handleOpenExpiryPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        AnalyticsManager.logEvent('Expiry Popover Opened')
        event.preventDefault()
        setAnchorEl(event.currentTarget)
    }, [])

    const handleClosePopover = useCallback(() => {
        setAnchorEl(null);
    }, [])

    const handleChange = useCallback((expiry: number) => {
        onChange(expiry);
        setAnchorEl(null);
    }, [onChange])

    return  <Box
                justifyContent="center"
                display="flex"
                sx={{ width: '100%', minWidth: 0, maxWidth: '100%' }}
            >
                <ExtraSmallButton
                    variant="outlined"
                    color={'inherit'}
                    disabled={disabled}
                    onClick={(e) => handleOpenExpiryPopover(e)}
                    sx={{
                        height: '24px',
                        padding: 0,
                        minWidth: 0,
                        maxWidth: '100%',
                        width: '100%',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            mx: 0.5,
                            fontWeight: 400,
                            display: 'block',
                            minWidth: 0,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                        }}
                        className="order-buttons"
                    >
                        {createStringDateFromStamp(security?.expiry?.expiry ?? 0)}
                    </Box>
                </ExtraSmallButton>
                <Popover
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                    }}
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClosePopover}
                >
                    {
                        expiries.map((ex) => (
                            <ExpiryMenuItem
                                value={ex.value}
                                label={ex.label}
                                data-event-source={'Trade'}
                                key={ex.value}
                                selected={ex.value === security?.expiry?.expiry}
                                onExpirySelect={handleChange}
                                className="font-feature-num" />))
                    }
                </Popover>
            </Box>
}

export default memo(ExpiryCell);

export type ExpiryMenuItemProps = {
    value: number,
    label: string,
    onExpirySelect: (expiry: number) => void
} & MenuItemProps

const ExpiryMenuItem: React.FC<ExpiryMenuItemProps> =  memo(({value, label, onExpirySelect, ...props}) => {
    const onSelect = useCallback(() => {onExpirySelect(value)}, [onExpirySelect])
    return <MenuItem data-event={'Expiry Changed'} onClick={onSelect} {...props}>{label}</MenuItem>;
});

ExpiryMenuItem.displayName = "ExpiryMenuItem";